// NOTE: price display in product detail page
import { SmartTooltip } from '@/components/extension/smart-tooltip'
import { Skeleton } from '@/components/ui/skeleton'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { useCampaignPrice } from '@/hooks/common/use-campaign-price'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn, formatPrice } from '@/lib/utils'
import { ProductFormatted } from '@/types/product'
import { ProductDetailFormatted } from '@/types/product-detail'
import { InfoIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

type Props = {
  regularPrice: number
  comparePrice: number
  salePercent?: number
  isCoolClubSale?: boolean
  tags?: Record<string, string>
  product: ProductFormatted | ProductDetailFormatted
  rankingsText?: string
}

/**
 * Hiển thị giá sản phẩm chi tiết, ưu tiên giá campaign nếu đang active.
 * Nếu campaign đang active, lấy giá campaign (finalPrice), giá gốc (originalPrice), phần trăm giảm (discountPercent).
 * Nếu đang loading campaign, hiển thị skeleton.
 */
export const ProductInfoPrice = ({
  regularPrice,
  comparePrice,
  salePercent,
  isCoolClubSale,
  tags,
  product,
  rankingsText = 'CoolClub',
}: Props) => {
  // Lấy danh sách tag sản phẩm
  const productTags = Array.isArray(tags) ? tags : Object.keys(tags || {})

  const t = useTranslations('product_detail.title')

  const tooltipContent = t('coolclub_price', {
    rankings: rankingsText,
  })

  // Lấy giá campaign nếu có
  const {
    finalPrice: campaignPrice,
    originalPrice: campaignOriginalPrice,
    discountPercent: campaignDiscountPercent,
    shouldShowDiscount: campaignShouldShowDiscount,
    isLoading: isCampaignLoading,
  } = useCampaignPrice({
    regularPrice: regularPrice,
    comparePrice: comparePrice,
    product: product,
  })

  // Nếu campaign đang active thì lấy giá campaign, ngược lại lấy giá thường
  const displayPrice = campaignShouldShowDiscount ? campaignPrice : regularPrice
  // Giá gạch ngang: nếu campaign active và giá gốc khác giá campaign thì lấy giá gốc, ngược lại lấy comparePrice
  const displayComparePrice =
    campaignShouldShowDiscount && campaignOriginalPrice !== campaignPrice
      ? campaignOriginalPrice
      : comparePrice
  // Phần trăm giảm: nếu campaign active và có giảm giá thì lấy campaignDiscountPercent, ngược lại lấy salePercent truyền vào
  const displayDiscountPercent =
    campaignShouldShowDiscount && campaignDiscountPercent > 0
      ? campaignDiscountPercent
      : salePercent

  return (
    <div className='mt-4 lg:mt-5'>
      {/* Hiển thị skeleton khi đang loading campaign price */}
      {isCampaignLoading ? (
        <div className='flex flex-col gap-2'>
          <Skeleton className='mb-1 h-4 w-24 lg:mb-2' />
          <div className='flex items-center gap-2 lg:gap-4'>
            {isCoolClubSale ? (
              <Skeleton className='h-6 w-6 rounded-full' />
            ) : null}
            <Skeleton className='h-7 w-28 rounded' />
            <Skeleton className='h-5 w-10 rounded' />
          </div>
        </div>
      ) : (
        <>
          {/* Hiển thị giá gạch ngang nếu có giảm giá */}
          {displayComparePrice && displayComparePrice !== displayPrice ? (
            <del className='mb-1 font-sans text-xs text-neutral-400 lg:mb-2 lg:text-base'>
              {formatPrice(displayComparePrice)}
            </del>
          ) : null}
          <div className='flex items-center gap-2 lg:gap-4'>
            {isCoolClubSale ? (
              <Image
                src={ImageHelper.getImageUrl(
                  '/image/December2024/mceclip0_21.png',
                  '',
                  48
                )}
                alt='Price'
                width={24}
                height={24}
                unoptimized
                className='h-6 w-6'
              />
            ) : null}
            <p
              className={cn(
                'font-sans text-lg font-bold leading-3 text-neutral-900 lg:text-[28px] lg:leading-5',
                isCoolClubSale ? 'text-primary' : ''
              )}
            >
              {formatPrice(displayPrice)}
            </p>
            <div className='flex items-center gap-1'>
              {displayDiscountPercent && displayDiscountPercent > 0 ? (
                <TagThumbnail
                  title={`-${displayDiscountPercent}%`}
                  size='small'
                  displayDiscountPercent={displayDiscountPercent}
                  className='lg:h-7 lg:text-base lg:leading-5'
                />
              ) : null}
              {productTags.includes('Good Price') ||
              productTags.includes('goodprice') ? (
                <SmartTooltip
                  content={t('good_price')}
                  side='top'
                  trigger='click'
                >
                  <InfoIcon className='h-3.5 w-3.5 cursor-pointer' />
                </SmartTooltip>
              ) : null}
            </div>
            {isCoolClubSale ? (
              <SmartTooltip
                content={tooltipContent}
                side='top'
                trigger='click'
              >
                <InfoIcon className='h-3.5 w-3.5 cursor-pointer' />
              </SmartTooltip>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}
